import { useState } from 'react';
import { matchesScope } from '../lib/tenantScope';
import { trunkStatusColor } from '../lib/trunkStatus';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, ChevronDown, Phone, Trash2 } from 'lucide-react';
import { getToken } from '../lib/api';
import { useToast } from '../components/Toast';
import SbcOnboarding from './SbcOnboarding';
import DectFxs from './DectFxs';
import TwilioSetup from './TwilioSetup';
import { SmsPane } from '../webclient/Chat';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

// Provider list is NOT hardcoded — it comes from the .pv.xml templates via
// GET /api/trunk-templates (see ChooseProviderModal below).

// MessagingTab wraps SmsPane (which uses h-full flex layout) in a sized container
// so that its internal scroll regions work correctly inside the tab panel.
function MessagingTab() {
  return (
    <div className="h-[600px] border border-border rounded-lg overflow-hidden">
      <SmsPane />
    </div>
  );
}

// ── Voice & Chat: one nav entry, tabs for each voice entity ──────────────────
const TABS: { key: string; label: string; Comp: any }[] = [
  { key: 'trunks',    label: 'Trunks',     Comp: TrunksTab },
  { key: 'twilio',    label: 'Twilio',     Comp: TwilioSetup },
  { key: 'sbcs',      label: 'SBCs',       Comp: SbcOnboarding },
  { key: 'messaging', label: 'Messaging',  Comp: MessagingTab },
  { key: 'fax',       label: 'Fax / DECT', Comp: DectFxs },
];

export default function VoiceChat() {
  const [tab, setTab] = useState('trunks');
  const Active = (TABS.find(t => t.key === tab) || TABS[0]).Comp;
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Voice &amp; Chat</h1>
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
              tab === t.key ? 'border-primary text-primary' : 'border-transparent text-textSecondary hover:text-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <Active onNavigateTab={setTab} />
    </div>
  );
}

function TrunksTab({ onNavigateTab }: { onNavigateTab?: (key: string) => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Aggregated list = trunks per tenant.
  const items = useQuery<any[]>({
    queryKey: ['voice-chat-list'],
    queryFn: async () => {
      const tenants: any[] = await fJson('/api/tenants');
      const out: any[] = [];
      for (const t of tenants) {
        try {
          const trunks: any[] = await fJson(`/api/tenants/${t.domain_uuid}/trunks`).catch(() => []);
          for (const tr of trunks) out.push({ ...tr, type: 'Trunk', _id: tr.gateway_uuid, dept: t.domain_description || t.domain_name, tenant_uuid: t.domain_uuid });
        } catch {}
      }
      return out;
    },
    refetchInterval: 30000,
  });

  const filtered = (items.data || []).filter(it => matchesScope(it.tenant_uuid)).filter(it =>
    !search || (it.gateway || '').toLowerCase().includes(search.toLowerCase())
            || (it.description || '').toLowerCase().includes(search.toLowerCase())
            || (it.gateway_name || '').toLowerCase().includes(search.toLowerCase())
            || (it.dept || '').toLowerCase().includes(search.toLowerCase())
            || (it.proxy || '').toLowerCase().includes(search.toLowerCase()));

  async function delTrunk(it: any) {
    const label = it.description || it.gateway_name || it.gateway || 'this trunk';
    if (!confirm(`Delete "${label}"? Its DID inbound routes are removed too — inbound to those numbers stops until re-provisioned. This cannot be undone.`)) return;
    setDeleting(it._id);
    try {
      const r = await fetch(`/api/trunks/${it._id}`, { method: 'DELETE', headers: auth().headers });
      if (!r.ok) throw new Error(await r.text());
      toast('success', 'Trunk deleted');
      items.refetch();
    } catch (e: any) { toast('error', String(e.message || e)); }
    finally { setDeleting(null); }
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        <button className="btn-primary inline-flex items-center gap-2" onClick={() => setShowAddProvider(true)}>
          <Plus size={14} /> Add Trunk
        </button>
        <button className="btn inline-flex items-center gap-2" onClick={() => onNavigateTab?.('twilio')}>
          <Phone size={14} /> Auto-setup with Twilio
        </button>
      </div>

      <div className="flex items-center gap-2 max-w-md">
        <Search size={16} className="text-textSecondary" />
        <input className="input flex-1" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" />
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th></th><th>Name</th><th>Information</th><th>Department</th><th>Route to</th><th className="w-8"></th></tr></thead>
          <tbody>
            {filtered.map((it: any) => {
              const dot = trunkStatusColor(it);
              return (
              <tr key={`${it.type}-${it._id}`} className="hover:bg-elevated cursor-pointer"
                  onClick={() => navigate(`/voice-chat/trunk/${it._id}/edit`)}>
                <td><span className={`inline-block w-3 h-3 rounded-full ${dot.cls}`} title={dot.label} /></td>
                <td>{it.description || it.gateway_name || it.gateway || it.name} <span className="text-textSecondary text-xs">({it.type})</span></td>
                <td className="font-mono text-xs">{it.gateway || it.proxy || '—'}</td>
                <td className="text-xs">{it.dept || ''}</td>
                <td className="text-xs">{it.route_to || it.default_route || '—'}</td>
                <td onClick={e => e.stopPropagation()}>
                  <button className="btn-ghost p-1 text-danger" title="Delete trunk"
                          disabled={deleting === it._id} onClick={() => delTrunk(it)}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        {!filtered.length && <div className="p-6 text-center text-textSecondary">No trunks yet.</div>}
      </div>

      {showAddProvider && <ChooseProviderModal onClose={() => setShowAddProvider(false)} />}
    </div>
  );
}

function ChooseProviderModal({ onClose }: any) {
  const navigate = useNavigate();
  const [filename, setFilename] = useState('');
  // The 23+ providers come from the .pv.xml templates — one source of truth.
  const templates = useQuery<any>({ queryKey: ['trunk-templates'], queryFn: () => fJson('/api/trunk-templates') });
  const builtin: any[] = (templates.data || {}).builtin || [];
  const custom: any[] = (templates.data || {}).custom || [];
  const selected = [...builtin, ...custom].find(t => t.filename === filename);

  function next() {
    if (!filename) return;
    navigate(`/voice-chat/trunk/new/${filename}`);
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="card max-w-md w-full p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-medium">Choose your SIP Trunk VoIP Provider</h2>
        <div>
          <label className="block text-sm text-textSecondary mb-1">Provider</label>
          <select className="input" value={filename} onChange={e => setFilename(e.target.value)}>
            <option value="">{templates.isLoading ? 'Loading providers…' : '— Choose a provider —'}</option>
            {builtin.map(t => <option key={t.filename} value={t.filename}>{t.name}</option>)}
            {custom.length > 0 && (
              <optgroup label="Custom (uploaded)">
                {custom.map(t => <option key={t.filename} value={t.filename}>{t.name}</option>)}
              </optgroup>
            )}
          </select>
        </div>
        {selected && (
          <p className="text-xs text-textSecondary">
            {selected.description ? `Coverage: ${selected.description}. ` : ''}
            {selected.url && (
              <a href={selected.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                {selected.name} configuration guide
              </a>
            )}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={next} disabled={!filename} className="btn-primary">OK</button>
        </div>
      </div>
    </div>
  );
}
